import React, { useEffect, useMemo, useState } from 'react';
import {
    Alert, AlertTitle, Box, Button, Chip, CircularProgress, FormControl, FormHelperText, Grid, InputLabel,
    MenuItem, Paper, Select, TextField, Tooltip, Typography
} from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import { observer } from 'mobx-react-lite';
import audioSettingsStore from '../../Mobx/AudioSettingsStore';
import generateAudioStore from '../../Mobx/GenerateAudioStore';
import { LANGUAGE_INFO, LANGUAGE_KEYS, VOICE_INFO, formatError, voiceLabel } from '../../Mobx/AudioCatalog';
import {
    buildPatch, formFromView, getUnavailableReason, isFormChanged, patchPaths, validateForm
} from './audioSettingsForm';
import useServerForm from './useServerForm';

/**
 * The settings of Google (Gemini) for the automatic creation of the audio files: the model, the prompt and the voices of each language.
 * What is saved here is what the server uses every time a word gets its audio files from Google (ai_settings.audio.speech.google).
 * The "בדיקה" buttons create one real audio file with the values that are in the form (even before they are saved).
 */

const SAVED_MESSAGE_MS = 6000;

const MODEL_NOTE = (
    <>
        דרך <bdi>Gemini API</bdi>: הקובץ מתקבל כ-WAV והשרת ממיר אותו ל-MP3 (מונו, 64kbps) לפני השמירה.
        השפה מזוהה מתוך הטקסט, ואין שליטה מספרית במהירות - מבקשים אותה בפרומפט (למשל: Speak slowly).
    </>
);

const QUOTA_NOTE = 'הבקשות ל-Gemini API מוגבלות לפי ה-Tier של הפרויקט ב-AI Studio, לכל מודל בנפרד. '
    + 'נכון ל-1.11.2026 (לפי התיעוד של Google): ב-Tier 1 כ-10 בקשות לדקה ו-100 ביום, '
    + 'ב-Tier 2 כ-1,000 לדקה ו-10,000 ביום, וב-Tier 3 כ-1,000 לדקה בלי תקרה יומית. '
    + 'כשהמכסה נגמרת מתקבלת שגיאה 429, והמילים האלה נשארות בלי אודיו. '
    + 'לפני יצירה בכמויות כדאי לבדוק את המכסה העדכנית ב-AI Studio.';

// ____________________________________________________________________________________________

const VoiceSelect = ({ id, label, value, voices, onChange }) => (
    <FormControl size="small" fullWidth>
        <InputLabel id={`${id}-label`}>{label}</InputLabel>
        <Select
            labelId={`${id}-label`}
            id={id}
            label={label}
            value={voices.includes(value) ? value : ''}
            onChange={(e) => onChange(e.target.value)}
        >
            {voices.map((voice) => (
                <MenuItem key={voice} value={voice}>
                    <Tooltip title={VOICE_INFO[voice]?.description || ''} placement="left" describeChild>
                        <span>{voiceLabel(voice)}</span>
                    </Tooltip>
                </MenuItem>
            ))}
        </Select>
    </FormControl>
);

/** Creates one audio file with the values of the form and plays it */
const SampleButton = ({ disabled, buildQuery }) => {
    const [state, setState] = useState({ status: 'idle' });

    const run = async () => {
        const query = buildQuery();
        setState({ status: 'loading' });
        const { sample, error } = await generateAudioStore.requestAudio(query);
        setState(error
            ? { status: 'error', error }
            : { status: 'ready', sample, caption: `${query.model} · ${query.name} · ${(sample.ms / 1000).toFixed(1)} s` });
    };

    return (
        <Box sx={{ mt: 1 }}>
            <Button
                size="small"
                variant="outlined"
                onClick={run}
                disabled={disabled || state.status === 'loading'}
                startIcon={state.status === 'loading' ? <CircularProgress size={14} /> : <PlayArrowIcon />}
            >
                בדיקה
            </Button>
            {state.status === 'ready' && (
                <Box sx={{ mt: 0.5 }}>
                    <audio controls autoPlay src={state.sample.url} style={{ width: '100%', height: 36 }} />
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}><bdi>{state.caption}</bdi></Typography>
                </Box>
            )}
            {state.status === 'error' && (
                <Typography variant="caption" color="error" dir="auto" sx={{ display: 'block', mt: 0.5 }}>
                    {formatError(state.error)}
                </Typography>
            )}
        </Box>
    );
};

// ____________________________________________________________________________________________

const AudioSettings = observer(() => {
    const store = audioSettingsStore;
    const { view, loading, loadError, loadCount, saving, saveError } = store;
    // The form starts from the settings that the server really uses, and starts again after its own save or a reload
    // (not after a save in the other tabs: the screens of all the tabs stay mounted and keep their unsaved changes)
    const [form, setForm] = useServerForm(view ? { effective: view.effective, configured: view.configured } : null, formFromView, loadCount);
    const [samples, setSamples] = useState(() => Object.fromEntries(LANGUAGE_KEYS.map((key) => [key, LANGUAGE_INFO[key].sample])));
    const [savedVisible, setSavedVisible] = useState(false);

    useEffect(() => {
        store.load();
    }, [store]);

    useEffect(() => {
        if (!savedVisible) return undefined;
        const timer = setTimeout(() => setSavedVisible(false), SAVED_MESSAGE_MS);
        return () => clearTimeout(timer);
    }, [savedVisible]);

    const patch = useMemo(() => (view && form ? buildPatch(form, view) : {}), [view, form]);
    const problems = useMemo(() => (view && form ? validateForm(form, view) : []), [view, form]);

    if (!view || !form) {
        if (loadError) {
            return (
                <Box sx={{ p: 2, maxWidth: 900, mx: 'auto' }}>
                    <Alert
                        severity="error"
                        action={<Button color="inherit" size="small" onClick={store.load} disabled={loading}>נסו שוב</Button>}
                    >
                        <AlertTitle>טעינת ההגדרות נכשלה</AlertTitle>
                        <Box dir="auto">{formatError(loadError)}</Box>
                        {loadError.status === 404 && (
                            <Box sx={{ mt: 1 }}>ייתכן שהשרת עדיין לא עודכן: חסר בו הנתיב GET /api/ai/settings/audio.</Box>
                        )}
                    </Alert>
                </Box>
            );
        }
        return (
            <Box sx={{ p: 4, display: 'flex', justifyContent: 'center' }}>
                <CircularProgress />
            </Box>
        );
    }

    const { effective, capabilities, catalog } = view;
    const model = catalog.models.find((entry) => entry.id === form.model);
    const paths = patchPaths(patch);
    const canSave = paths.length > 0 && problems.length === 0 && !saving;
    const voices = store.voices;

    const setField = (field, value) => setForm((current) => ({ ...current, [field]: value }));
    const setLanguageField = (key, field, value) => setForm((current) => ({ ...current, [key]: { ...current[key], [field]: value } }));

    const handleSave = async () => {
        if (await store.save(patch)) setSavedVisible(true);
    };

    const testQuery = (key, voice) => () => ({
        text: samples[key],
        prompt: form.prompt,
        language: LANGUAGE_INFO[key].testLanguage,
        name: voice,
        model: form.model,
    });
    const testDisabled = (key) => Boolean(getUnavailableReason(model, capabilities))
        || !model || !form.prompt.trim() || !samples[key].trim();

    return (
        <Box dir="rtl" sx={{ p: 2, maxWidth: 1100, mx: 'auto' }}>
            <Typography variant="h5" gutterBottom>הגדרות Google (Gemini)</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                ההגדרות האלה חלות על כל תא ב"כללי יצירה אוטומטית" שהספק שלו הוא Google: המודל, הפרומפט והקולות של כל שפה.
                לבדיקה חופשית של קול, מודל ופרומפט בלי לשמור כלום, השתמשו בלשונית "בדיקת יצירה".
            </Typography>

            {/* __________ מצב נוכחי __________ */}
            <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1.5 }}>
                    <Chip
                        size="small"
                        color={capabilities.ffmpeg ? 'success' : 'error'}
                        label={`ffmpeg בשרת: ${capabilities.ffmpeg ? 'זמין' : 'חסר'}`}
                    />
                    <Chip
                        size="small"
                        color={capabilities.geminiApiKey ? 'success' : 'error'}
                        label={`מפתח Gemini API: ${capabilities.geminiApiKey ? (capabilities.dedicatedTtsKey ? 'ייעודי ל-TTS' : 'המפתח הכללי') : 'חסר'}`}
                    />
                    <Chip size="small" variant="outlined" label={<bdi>{`המודל הפעיל כעת: ${effective.model}`}</bdi>} />
                </Box>
                {!capabilities.ffmpeg && (
                    <Alert severity="error" sx={{ mb: 1.5 }}>
                        <AlertTitle>ffmpeg חסר בשרת</AlertTitle>
                        בלי ffmpeg אי אפשר ליצור את קובצי ה-MP3 של המילון מהאודיו של Google, ולכן היצירה האוטומטית עם Google תיכשל
                        (Inworld מחזיר MP3 ישירות ואינו תלוי ב-ffmpeg).
                        אפשר לשמור את ההגדרות, ולשונית "בדיקת יצירה" ממשיכה לעבוד (היא מנגנת את קובץ ה-WAV כמו שהוא).
                    </Alert>
                )}
                {effective.fellBack ? (
                    <Alert severity="warning">
                        <AlertTitle>ההגדרות השמורות במסד הנתונים אינן תקינות</AlertTitle>
                        כל עוד זה כך, היצירה האוטומטית משתמשת בערכי ברירת מחדל (המודל <bdi>{effective.model}</bdi>) במקום להיכשל.
                        לחצו על "שמירה" כדי להחליף את הערכים הלא תקינים בערכים המוצגים למטה.
                        <Box component="ul" dir="ltr" sx={{ mt: 1, mb: 0, paddingInlineStart: '24px', fontSize: 13 }}>
                            {effective.warnings.map((warning) => <li key={warning}>{warning}</li>)}
                        </Box>
                    </Alert>
                ) : (
                    <Alert severity="success">
                        ההגדרות תקינות. כל יצירה אוטומטית חדשה עם Google משתמשת בהן מיד.
                    </Alert>
                )}
            </Paper>

            {/* __________ מודל וסגנון __________ */}
            <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                <Typography variant="h6" gutterBottom>מודל וסגנון</Typography>
                <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                        <FormControl size="small" fullWidth>
                            <InputLabel id="audio-settings-model-label">מודל</InputLabel>
                            <Select
                                labelId="audio-settings-model-label"
                                id="audio-settings-model"
                                label="מודל"
                                value={model ? model.id : ''}
                                onChange={(e) => setField('model', e.target.value)}
                            >
                                {catalog.models.map((entry) => {
                                    const reason = getUnavailableReason(entry, capabilities);
                                    return (
                                        <MenuItem key={entry.id} value={entry.id} disabled={Boolean(reason)}>
                                            <bdi>{entry.label}</bdi>{reason ? ` - לא זמין: ${reason}` : ''}
                                        </MenuItem>
                                    );
                                })}
                            </Select>
                            <FormHelperText>
                                {model ? MODEL_NOTE : ''}
                                {model?.preview ? ' (מודל Preview - עלול להשתנות או להיעלם.)' : ''}
                            </FormHelperText>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12}>
                        <Alert severity="warning">{QUOTA_NOTE}</Alert>
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            label="פרומפט (הנחיית סגנון לקריינות)"
                            size="small"
                            fullWidth
                            multiline
                            minRows={2}
                            value={form.prompt}
                            onChange={(e) => setField('prompt', e.target.value)}
                            inputProps={{ dir: 'auto' }}
                            helperText={
                                'כתבו באנגלית. '
                                + 'לטקסט באנגלית מתווספת אוטומטית הוראת מבטא (אמריקאי/בריטי), אלא אם הפרומפט כבר מזכיר accent, British או American. '
                                + `${new TextEncoder().encode(form.prompt).length} / ${catalog.limits?.promptBytes ?? 4000} בייט`
                            }
                        />
                    </Grid>
                </Grid>
            </Paper>

            {/* __________ קולות __________ */}
            <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                <Typography variant="h6" gutterBottom>קולות לפי שפה</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                    כל לחיצה על "בדיקה" יוצרת קובץ אחד באמת: היא עולה כסף ונספרת במכסה. הבדיקה משתמשת בערכים שבטופס, גם לפני שמירה.
                </Typography>
                {LANGUAGE_KEYS.map((key) => {
                    const info = LANGUAGE_INFO[key];
                    return (
                        <Paper key={key} variant="outlined" sx={{ p: 2, mb: 1.5, bgcolor: 'grey.50' }}>
                            <Grid container spacing={2} alignItems="flex-start">
                                <Grid item xs={12} md={2}>
                                    <Typography sx={{ fontWeight: 'bold' }}>{info.label}</Typography>
                                </Grid>
                                <Grid item xs={12} md={4}>
                                    <VoiceSelect
                                        id={`audio-settings-${key}-female`}
                                        label="קול נשי"
                                        value={form[key].female}
                                        voices={voices}
                                        onChange={(value) => setLanguageField(key, 'female', value)}
                                    />
                                    <SampleButton disabled={testDisabled(key)} buildQuery={testQuery(key, form[key].female)} />
                                </Grid>
                                <Grid item xs={12} md={4}>
                                    <VoiceSelect
                                        id={`audio-settings-${key}-male`}
                                        label="קול גברי"
                                        value={form[key].male}
                                        voices={voices}
                                        onChange={(value) => setLanguageField(key, 'male', value)}
                                    />
                                    <SampleButton disabled={testDisabled(key)} buildQuery={testQuery(key, form[key].male)} />
                                </Grid>
                                <Grid item xs={12} md={2}>
                                    <TextField
                                        label="מילה לבדיקה"
                                        size="small"
                                        fullWidth
                                        value={samples[key]}
                                        onChange={(e) => setSamples((current) => ({ ...current, [key]: e.target.value }))}
                                        inputProps={{ dir: 'auto' }}
                                    />
                                </Grid>
                            </Grid>
                        </Paper>
                    );
                })}
            </Paper>

            {/* __________ שמירה __________ */}
            <Paper
                elevation={3}
                sx={{ p: 2, position: 'sticky', bottom: 8, zIndex: 2, display: 'flex', flexDirection: 'column', gap: 1 }}
            >
                {problems.length > 0 && (
                    <Alert severity="error">
                        <Box component="ul" sx={{ m: 0, paddingInlineStart: '24px' }}>
                            {problems.map((problem) => <li key={problem}>{problem}</li>)}
                        </Box>
                    </Alert>
                )}
                {saveError && (
                    <Alert severity="error">
                        <AlertTitle>השמירה נכשלה</AlertTitle>
                        <Box dir="auto">{formatError(saveError)}</Box>
                    </Alert>
                )}
                {savedVisible && (
                    <Alert severity="success" onClose={() => setSavedVisible(false)}>
                        נשמר. ההגדרות החדשות חלות מיד על כל יצירה אוטומטית חדשה עם Google.
                    </Alert>
                )}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                    <Button variant="contained" onClick={handleSave} disabled={!canSave}>
                        {saving ? 'שומר...' : 'שמירה'}
                    </Button>
                    <Button variant="outlined" onClick={() => setForm(formFromView(view))} disabled={saving || !isFormChanged(form, view)}>
                        ביטול שינויים
                    </Button>
                    <Button variant="text" onClick={store.load} disabled={loading || saving}>
                        טעינה מחדש מהשרת
                    </Button>
                    <Typography variant="body2" color="text.secondary" dir="auto">
                        {paths.length > 0 ? `יישמרו: ${paths.join(', ')}` : 'אין מה לשמור'}
                    </Typography>
                </Box>
            </Paper>

            {/* __________ פרטים טכניים __________ */}
            <Box component="details" sx={{ mt: 2, mb: 4 }}>
                <Box component="summary" sx={{ cursor: 'pointer', color: 'text.secondary' }}>פרטים טכניים: מה שמור כרגע במסד הנתונים</Box>
                <Box component="pre" dir="ltr" sx={{ fontSize: 12, overflow: 'auto', bgcolor: 'grey.100', p: 1, borderRadius: 1 }}>
                    {JSON.stringify(view.configured, null, 2)}
                </Box>
            </Box>
        </Box>
    );
});

export default AudioSettings;
